import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminUser } from "../types";

export type UserList = Paginate<AdminUser<true>>;

export const users = createQueryKeys("admin-users", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "position,roles,permissions";

export const fetchUsersQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...users.all(page, search),
    queryFn: () =>
      adminApi
        .get<UserList>(`/users`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchUsers = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchUsersQueryOptions(page, search));

export const fetchUserQueryOptions = (id?: number) =>
  queryOptions({
    ...users.one(id),
    queryFn: () =>
      adminApi
        .get<AdminUser<true>>(`/users/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchUser = (id?: number) =>
  useQuery(fetchUserQueryOptions(id));

export interface UserRequest {
  first_name: string;
  last_name: string;
  email: string;
  roles?: number[] | null;
  personnel_code: string;
  mobile: string;
  gender?: "male" | "female" | null;
  birth_date?: string | null;
  home_phone?: string | null;
  work_phone?: string | null;
  address?: string | null;
  work_email?: string | null;
  employment_type_entity_id?: number | null;
  work_location_entity_id?: number | null;
  cost_center_entity_id?: number | null;
  password?: string | null;
}

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutationWithError({
    mutationFn: (data: UserRequest) =>
      adminApi.post(`/users`, data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: users.all._def,
      });
    },
  });
};

export const useUpdateUser = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UserRequest) =>
      adminApi.put(`/users/${id}`, data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: users.all._def,
      });
      queryClient.invalidateQueries(users.one(id));
    },
  });
};

export const useCreateOrUpdateUser = (id: number | undefined) => {
  const create = useCreateUser();
  const update = useUpdateUser(id!);
  return id ? update : create;
};
